// Phase 10 Plan 01 Task 3 — ExtractionMeta envelope schema (AI-06 / AI-11).
//
// The metadata envelope that travels alongside an AI-extracted recipe-v1 body
// from the provider adapter (Plan 10-08 extract route) into the audit row
// (ai_extraction_audit) and through the reviewer surface (Plans 10-10..13).
//
// PROJECT-WIDE CONTRACT — same envelope rendered by:
//   - apps/web review surface ExtractionMetadataBar (Plan 10-11)
//   - apps/api ai_extraction_audit row's evidence column (Plan 10-02 schema)
//   - apps/api extract route response body (Plan 10-08)
//   - admin AI config rotation log (Plan 10-09)
//
// AI-06 (Refusal handling): ExtractionMeta does NOT carry refusal fields. The
// failure category lives on ai_extraction_audit.error_category (one of
// AI_ERROR_CATEGORIES from error-taxonomy.ts). A successful extraction
// produces ExtractionMeta + a recipe body; a failed extraction produces an
// audit row with error_category set and NO ExtractionMeta promotion.
//
// AI-11 (Source attestation): EXACTLY ONE of sourceUrl / sourceContentHash MUST
// be populated by the extract route handler (Plan 10-08 enforces this at the
// route boundary — the schema here permits both to be optional so the
// admin-rotation audit row can carry an ExtractionMeta-shaped value without
// either field, but production-promoted rows always carry one or the other).
// The ceiling-complexity acceptance test fixtures must verify this invariant.
//
// PARANOID-STRICT compliance (Phase 01.1 D-15):
//   isolatedDeclarations:        explicit `export interface ExtractionMeta` +
//                                Zod const annotated as z.ZodType<ExtractionMeta>
//                                so tsc emits declarations without crossing
//                                module boundaries.
//   verbatimModuleSyntax:        `import { z } from "zod"` (no `import type`
//                                because we use z at runtime).
//   exactOptionalPropertyTypes:  optional fields use `T | undefined` to match
//                                ZodOptional's output. Omit the key entirely
//                                for absent fields — NEVER pass
//                                `{sourceUrl: undefined}` (that is a type error
//                                under exactOptionalPropertyTypes).
//   noUncheckedIndexedAccess:    N/A (no index access here).

import { z } from "zod";

// ExtractionMeta — the envelope written alongside an extracted recipe-v1 body.
//
// FIELD CONTRACT:
//   sourceUrl            URL the extraction was sourced from. z.string().url()
//                        rejects javascript:/data:/etc by default behavior —
//                        only http(s) absolute URLs pass (PATTERNS Pitfall 6).
//   sourceContentHash    sha256 hex of the raw input bytes when sourceUrl is
//                        absent (i.e. paste-from-clipboard or file upload).
//                        Fixed 64-char hex regex; nothing else accepted.
//   provider             which AI provider produced the extraction. Closed
//                        2-enum locked to "anthropic" | "openai" — adding a
//                        third provider is a deliberate breaking change.
//   model                provider-specific model id (e.g. claude-sonnet-4-6,
//                        gpt-5-mini). Bounded length so a rotated id cannot
//                        balloon the audit row.
//   extractedAt          ISO 8601 timestamp the extraction completed at. The
//                        audit row uses this to order retry attempts.
//   promptTokenCount     provider-reported prompt token usage (cost telemetry).
//   completionTokenCount provider-reported completion token usage (cost telemetry).
export interface ExtractionMeta {
  sourceUrl?: string | undefined;
  sourceContentHash?: string | undefined;
  provider: "anthropic" | "openai";
  model: string;
  extractedAt: string;
  promptTokenCount?: number | undefined;
  completionTokenCount?: number | undefined;
}

// sourceUrl URL-scheme allowlist (PATTERNS Pitfall 6). Zod 4's z.string().url()
// accepts ANY valid URL including javascript: / data: / ftp: — that is unsafe
// for an audit-row field that may be re-rendered in the reviewer UI. Filter to
// http(s) only via an explicit refine. Failure message kept generic; the
// sanitization tier never echoes the offending value back to the client.
const _httpOrHttpsUrl = z
  .string()
  .url()
  .refine(
    (val) => {
      try {
        const u = new URL(val);
        return u.protocol === "http:" || u.protocol === "https:";
      } catch {
        return false;
      }
    },
    { message: "sourceUrl must use http(s) scheme" },
  );

// _extractionMetaSchema — private intermediate matching the recipe-v1.ts pattern.
// The public `ExtractionMeta` Zod alias re-exports under the interface name so
// downstream callers write `ExtractionMeta.parse(input)` AND consume the
// `ExtractionMeta` interface type from the same identifier.
//
// z.strictObject rejects unknown keys at the top level — defense-in-depth
// against a malformed provider response leaking unstructured fields into the
// audit row. Per AI-06 the schema does NOT carry refusal fields; refusal is
// recorded on ai_extraction_audit.error_category, not here.
const _extractionMetaSchema: z.ZodType<ExtractionMeta> = z.strictObject({
  sourceUrl: _httpOrHttpsUrl.optional(),
  sourceContentHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/, "sourceContentHash must be 64-char lowercase hex (sha256)")
    .optional(),
  provider: z.enum(["anthropic", "openai"]),
  model: z.string().min(1).max(80),
  extractedAt: z.string().datetime(),
  promptTokenCount: z.number().int().nonnegative().optional(),
  completionTokenCount: z.number().int().nonnegative().optional(),
});

export const ExtractionMeta: z.ZodType<ExtractionMeta> = _extractionMetaSchema;
